import { useCallback, useRef } from 'react';
import { driver, type Driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  PORTAL_PDF_MARKUP_TOUR_SELECTORS,
  buildPortalPdfMarkupTourSteps,
} from '../../utils/portalPdfMarkupTourSteps';
import './PortalPdfMarkupTour.css';

const STORAGE_KEY = 'lctf:portalPdfMarkupTour:v1';

let activeDriver: Driver | null = null;

function elementExists(selector: string): boolean {
  return typeof document !== 'undefined' && !!document.querySelector(selector);
}

function waitForElement(selector: string, timeoutMs = 2500): Promise<boolean> {
  return new Promise(resolve => {
    if (elementExists(selector)) {
      resolve(true);
      return;
    }
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (elementExists(selector)) {
        window.clearInterval(intervalId);
        resolve(true);
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(intervalId);
        resolve(false);
      }
    }, 60);
  });
}

function clickToolbarButton(selector: string): void {
  document.querySelector<HTMLButtonElement>(selector)?.click();
}

/** Hide pen/highlighter tools so the Draw mode toggle is spotlighted alone. */
function ensureCommentMode(): void {
  if (elementExists(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools)) {
    clickToolbarButton(PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeComment);
  }
}

/** Reveal pen/highlighter tools for the draw-tools spotlight step. */
function ensureDrawMode(): void {
  if (!elementExists(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools)) {
    clickToolbarButton(PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeDraw);
  }
}

function waitForDrawToolsHidden(timeoutMs = 1200): Promise<void> {
  return new Promise(resolve => {
    if (!elementExists(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools)) {
      resolve();
      return;
    }
    const startedAt = Date.now();
    const intervalId = window.setInterval(() => {
      if (!elementExists(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools)) {
        window.clearInterval(intervalId);
        resolve();
      } else if (Date.now() - startedAt > timeoutMs) {
        window.clearInterval(intervalId);
        resolve();
      }
    }, 40);
  });
}

function refreshTourLayout(d: Driver): void {
  window.requestAnimationFrame(() => {
    if (d.isActive()) d.refresh();
  });
}

function stepSelector(step: DriveStep | undefined): string | undefined {
  return typeof step?.element === 'string' ? step.element : undefined;
}

function withToolbarModeSync(steps: DriveStep[]): DriveStep[] {
  return steps.map(step => {
    const selector = stepSelector(step);

    if (selector === PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeComment) {
      return {
        ...step,
        onHighlightStarted: (_el, _s, { driver: d }) => {
          void (async () => {
            ensureCommentMode();
            await waitForDrawToolsHidden();
            refreshTourLayout(d);
          })();
        },
      };
    }

    if (selector === PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeDraw) {
      return {
        ...step,
        disableActiveInteraction: true,
        onHighlightStarted: (_el, _s, { driver: d }) => {
          void (async () => {
            ensureCommentMode();
            await waitForDrawToolsHidden();
            refreshTourLayout(d);
          })();
        },
      };
    }

    if (selector === PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools) {
      return {
        ...step,
        element: () => {
          ensureDrawMode();
          return (
            document.querySelector(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools) ??
            document.querySelector(PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeDraw)!
          );
        },
        onHighlightStarted: (_el, _s, { driver: d }) => {
          void (async () => {
            ensureDrawMode();
            await waitForElement(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools, 1200);
            refreshTourLayout(d);
          })();
        },
      };
    }

    return step;
  });
}

export function markPortalPdfMarkupTourSeen(): void {
  try {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
  } catch {
    /* ignore */
  }
}

export function hasSeenPortalPdfMarkupTour(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY);
  } catch {
    return false;
  }
}

export function destroyPortalPdfMarkupTour(): void {
  activeDriver?.destroy();
  activeDriver = null;
}

export function isPortalPdfMarkupTourActive(): boolean {
  return activeDriver?.isActive() ?? document.body.classList.contains('driver-active');
}

export interface StartPortalPdfMarkupTourOptions {
  hasPagination?: boolean;
  force?: boolean;
}

export function startPortalPdfMarkupTour(options: StartPortalPdfMarkupTourOptions = {}): void {
  const { hasPagination = false, force = false } = options;
  if (!force && hasSeenPortalPdfMarkupTour()) return;

  destroyPortalPdfMarkupTour();

  void (async () => {
    const ready = await waitForElement(PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeBar);
    if (!ready) {
      console.warn('[PortalPdfMarkupTour] Mode bar not found; tour not started.');
      return;
    }

    ensureCommentMode();
    const steps = withToolbarModeSync(buildPortalPdfMarkupTourSteps({ hasPagination }));
    if (steps.length === 0) return;

    const driverObj = driver({
      steps,
      showProgress: steps.length > 2,
      progressText: '{{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Got it',
      popoverClass: 'portal-pdf-tour-popover',
      smoothScroll: true,
      stagePadding: 8,
      stageRadius: 10,
      allowClose: true,
      overlayColor: '#1A1A1A',
      overlayOpacity: 0.55,
      onNextClick: (_el, step, { driver: d }) => {
        const selector = stepSelector(step);
        if (selector === PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeDraw) {
          ensureDrawMode();
          void waitForElement(PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools, 1200).then(() => {
            if (!d.isActive()) return;
            d.moveNext();
          });
          return;
        }
        if (d.isLastStep()) {
          d.destroy();
          return;
        }
        d.moveNext();
      },
      onPrevClick: (_el, step, { driver: d }) => {
        const selector = stepSelector(step);
        if (
          selector === PORTAL_PDF_MARKUP_TOUR_SELECTORS.drawTools ||
          selector === PORTAL_PDF_MARKUP_TOUR_SELECTORS.modeDraw
        ) {
          ensureCommentMode();
          void waitForDrawToolsHidden().then(() => {
            if (!d.isActive()) return;
            d.movePrevious();
            refreshTourLayout(d);
          });
          return;
        }
        d.movePrevious();
      },
      onCloseClick: (_el, _step, { driver: d }) => {
        d.destroy();
      },
      onDestroyed: () => {
        activeDriver = null;
        markPortalPdfMarkupTourSeen();
        ensureCommentMode();
      },
    });

    activeDriver = driverObj;
    driverObj.drive();
  })();
}

/** Hook reserved for future auto-start from splash; tour is triggered on first markup open. */
export function usePortalPdfMarkupTour(_enabled: boolean) {
  const driverRef = useRef<Driver | null>(null);
  const destroyTour = useCallback(() => {
    destroyPortalPdfMarkupTour();
    driverRef.current = null;
  }, []);
  return { destroyTour, startTour: startPortalPdfMarkupTour };
}
